// PostCallAnalysisCard.js
import React, { useState } from 'react';
import './PostCallAnalysisCard.css';

const MetricValue = ({ name, displayName, value, type }) => {
  const renderValue = () => {
    if (value === null || value === undefined) {
      return <span className="metric-value empty">—</span>;
    }

    switch (type) {
      case 'boolean':
        return (
          <span className={`metric-value boolean ${value ? 'yes' : 'no'}`}>
            {value ? 'Так' : 'Ні'}
          </span>
        );
      case 'numeric':
        return <span className="metric-value numeric">{value}</span>;
      case 'categorical':
        return (
          <span className={`metric-value categorical ${value}`}>
            {formatCategoricalValue(value)}
          </span>
        );
      case 'list':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <ul className="metric-value list">
              {value.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          );
        }
        return <span className="metric-value empty">—</span>;
      case 'text':
      default:
        return <span className="metric-value text">{value}</span>;
    }
  };

  return (
    <div className="metric-item">
      <div className="metric-name">{displayName}</div>
      {renderValue()}
    </div>
  );
};

const formatCategoricalValue = (value) => {
  const labels = {
    positive: 'Позитивний',
    neutral: 'Нейтральний',
    negative: 'Негативний',
    fully_resolved: 'Повністю вирішено',
    partially_resolved: 'Частково вирішено',
    not_resolved: 'Не вирішено',
  };
  return labels[value] || value;
};

const getMetricType = (name, value, metricMetadata) => {
  if (metricMetadata && metricMetadata[name]) {
    return metricMetadata[name].metric_type;
  }

  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'numeric';
  if (Array.isArray(value)) return 'list';
  return 'text';
};

const getDisplayName = (name, metricMetadata) => {
  if (metricMetadata && metricMetadata[name]) {
    return metricMetadata[name].display_name;
  }

  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const isTextMetric = (name, metricMetadata) => {
  const type = metricMetadata?.[name]?.metric_type;
  return type === 'text' || name === 'summary' || name === 'customer_intent';
};

const SinglePostCallAnalysisContent = ({ analysisResult }) => {
  if (!analysisResult || !analysisResult.is_successful) {
    return null;
  }

  const metrics = analysisResult.metric_values || {};
  const metricMetadata = analysisResult.metric_metadata || {};
  const metricEntries = Object.entries(metrics);

  if (metricEntries.length === 0) {
    return null;
  }

  const textMetrics = metricEntries.filter(([name]) => isTextMetric(name, metricMetadata));
  const quickMetrics = metricEntries.filter(([name]) => !isTextMetric(name, metricMetadata));

  return (
    <>
      {quickMetrics.length > 0 && (
        <div className="quick-metrics">
          {quickMetrics.map(([name, value]) => (
            <MetricValue
              key={name}
              name={name}
              displayName={getDisplayName(name, metricMetadata)}
              value={value}
              type={getMetricType(name, value, metricMetadata)}
            />
          ))}
        </div>
      )}

      {textMetrics.length > 0 && (
        <div className="text-metrics">
          {textMetrics.map(([name, value]) => (
            <MetricValue
              key={name}
              name={name}
              displayName={getDisplayName(name, metricMetadata)}
              value={value}
              type="text"
            />
          ))}
        </div>
      )}
    </>
  );
};

const PostCallAnalysisCard = ({ analysisResult, collapsible = true }) => {
  const [open, setOpen] = useState(!collapsible);

  if (!analysisResult || analysisResult.length === 0) return null;
  const successful = analysisResult.filter((r) => r?.is_successful === true);

  if (successful.length === 0) return null;

  const scrollEnabled = analysisResult.length > 1;
  const isOpen = collapsible ? open : true;

  return (
    <div className={`post-call-analysis-card ${isOpen ? 'is-open' : ''}`}>
      {collapsible ? (
        <button
          type="button"
          className="analysis-header"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          <div className="analysis-icon">📊</div>
          <h3>Аналіз розмови</h3>
          <span className="analysis-toggle" aria-hidden="true">
            <svg
              className="analysis-toggle-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      ) : (
        <div className="analysis-header static">
          <div className="analysis-icon">📊</div>
          <h3>Аналіз розмови</h3>
        </div>
      )}

      <div className={`post-call-analysis-collapse ${isOpen ? 'open' : ''}`}>
        <div className="post-call-analysis-collapse-inner">
          <div className={`post-call-analysis-body ${scrollEnabled ? 'scroll' : ''}`}>
            {analysisResult.map((res, idx) => (
              <React.Fragment key={res.key || idx}>
                {idx > 0 && <hr />}
                <SinglePostCallAnalysisContent analysisResult={res} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCallAnalysisCard;
